import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const INVITE_STORAGE_KEY = "lifeos:pending_invite_token";

export function stashPendingInvite(token) {
  try {
    sessionStorage.setItem(INVITE_STORAGE_KEY, token);
  } catch (_) {
    /* noop */
  }
}
export function popPendingInvite() {
  try {
    const t = sessionStorage.getItem(INVITE_STORAGE_KEY);
    if (t) sessionStorage.removeItem(INVITE_STORAGE_KEY);
    return t;
  } catch (_) {
    return null;
  }
}

const startGoogleLogin = () => {
  const redirectUrl = `${window.location.origin}/app`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function JoinHousehold() {
  const { token } = useParams();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [state, setState] = useState({ loading: true, error: null, invite: null, accepted: false });

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    if (!token) {
      setState({ loading: false, error: "Missing invite token.", invite: null, accepted: false });
      return;
    }
    stashPendingInvite(token);

    (async () => {
      let me = null;
      try {
        const r = await api.get("/auth/me");
        me = r.data;
      } catch (_) {
        me = null;
      }
      let inviteVerify;
      try {
        const r = await api.get(`/household/invites/verify`, { params: { token } });
        inviteVerify = r.data;
      } catch (e) {
        setState({ loading: false, error: e?.response?.data?.detail || "Could not verify this invite.", invite: null, accepted: false });
        return;
      }
      if (!inviteVerify.ok && !me) {
        // If wrong_email but user not logged in, that's actually fine — they may sign in with the right email.
        if (inviteVerify.reason === "wrong_email") {
          setState({ loading: false, error: null, invite: inviteVerify, accepted: false });
          return;
        }
        setState({ loading: false, error: reasonToMessage(inviteVerify.reason), invite: null, accepted: false });
        return;
      }
      if (!inviteVerify.ok && me) {
        // Logged in but wrong email or expired etc.
        setState({ loading: false, error: reasonToMessage(inviteVerify.reason, inviteVerify), invite: inviteVerify, accepted: false });
        return;
      }
      // Invite is valid.
      if (!me) {
        // Not signed in — stash + show sign-in prompt.
        setState({ loading: false, error: null, invite: inviteVerify, accepted: false });
        return;
      }
      // Signed in as the right email — accept immediately.
      try {
        await api.post("/household/invites/accept", { token });
        popPendingInvite();
        setState({ loading: false, error: null, invite: inviteVerify, accepted: true });
        toast.success(`Welcome to ${inviteVerify.household_name}.`);
        setTimeout(() => navigate("/app/tower", { replace: true }), 1200);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Could not accept invite.";
        setState({ loading: false, error: msg, invite: inviteVerify, accepted: false });
      }
    })();
  }, [token, navigate]);

  return (
    <main data-testid="join-page" className="relative min-h-screen overflow-hidden bg-obsidian">
      <div className="pointer-events-none absolute inset-0 hud-grid radial-fade opacity-30" />
      <div className="pointer-events-none absolute inset-x-0 top-1/3 h-[380px] bg-[radial-gradient(ellipse_at_center,rgba(46,119,136,0.14),transparent_70%)]" />

      <div className="relative z-10 mx-auto flex h-16 max-w-6xl items-center px-6 md:px-10">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="relative flex h-6 w-6 items-center justify-center border border-teal/70">
            <Compass className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            <span className="absolute -top-px -left-px h-1 w-1 bg-amber" />
          </span>
          <span className="font-display text-lg text-ink">Life OS</span>
        </Link>
      </div>

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl items-center px-6 pb-16 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full"
        >
          <span className="absolute -top-2 -left-2 h-4 w-4 border-t border-l border-teal/70" />
          <span className="absolute -top-2 -right-2 h-4 w-4 border-t border-r border-teal/70" />
          <span className="absolute -bottom-2 -left-2 h-4 w-4 border-b border-l border-teal/70" />
          <span className="absolute -bottom-2 -right-2 h-4 w-4 border-b border-r border-teal/70" />

          <div className="border border-rule bg-deep/80 p-10 backdrop-blur-md md:p-14">
            {state.loading && (
              <div className="text-center">
                <div className="mx-auto h-2 w-2 animate-pulse-soft bg-teal" />
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.26em] text-mist">
                  Verifying invitation…
                </p>
              </div>
            )}

            {!state.loading && state.error && !state.invite && (
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signalRed">
                  Invitation unavailable
                </p>
                <h1 className="mt-4 font-display text-3xl text-ink md:text-4xl">{state.error}</h1>
                <p className="mt-4 text-sm text-mist">
                  If you believe this is a mistake, ask the household owner to send you a fresh invitation.
                </p>
                <Link
                  to="/"
                  className="mt-8 inline-block border border-rule px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-mist hover:border-teal hover:text-ink"
                  data-testid="join-back-home"
                >
                  Back to landing
                </Link>
              </div>
            )}

            {!state.loading && state.invite && !state.accepted && (
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
                  {state.invite.ok ? "You’ve been invited" : "Sign-in mismatch"}
                </p>
                <h1 className="mt-4 font-display text-3xl leading-tight text-ink md:text-4xl">
                  {state.invite.inviter_name || "Someone"} added you to{" "}
                  <span className="italic text-mist">{state.invite.household_name}</span>.
                </h1>
                <p className="mt-4 text-base text-mist">
                  Life OS shows shared capacity, compression, and recovery across your household. Once you accept,
                  your calendar becomes part of the aggregate load. You can leave at any time.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <span className="border border-rule bg-deep px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                    Role · {(state.invite.role || "member").replace("_", "-")}
                  </span>
                  <span className="border border-rule bg-deep px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                    Sent to · {state.invite.invited_email}
                  </span>
                </div>

                {state.error && (
                  <p
                    data-testid="join-error"
                    className="mt-6 border border-signalRed/40 bg-signalRed/10 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-signalRed"
                  >
                    {state.error}
                  </p>
                )}

                <div className="mt-10 flex items-center gap-3">
                  <ShieldCheck className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                    Google sign-in required — use the email this invite was sent to.
                  </p>
                </div>
                <button
                  onClick={startGoogleLogin}
                  data-testid="join-signin-btn"
                  className="mt-4 inline-flex items-center gap-3 border border-rule bg-obsidian px-6 py-4 font-mono text-[12px] uppercase tracking-[0.24em] text-ink transition-colors duration-300 hover:border-teal hover:bg-deep"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M17.64 9.204c0-.638-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.616z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.68 9c0-.593.102-1.17.284-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>
              </div>
            )}

            {!state.loading && state.accepted && (
              <div className="text-center">
                <div className="mx-auto flex h-10 w-10 items-center justify-center border border-signalGreen text-signalGreen">
                  <CheckCircle2 className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.26em] text-signalGreen">
                  You’re aboard
                </p>
                <h1 className="mt-4 font-display text-3xl text-ink md:text-4xl">
                  Welcome to <span className="italic text-mist">{state.invite?.household_name}</span>.
                </h1>
                <p className="mt-4 text-sm text-mist">Routing you to the tower…</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </main>
  );
}

function reasonToMessage(reason, invite) {
  switch (reason) {
    case "invalid":
      return "This invite link is invalid or has already been used.";
    case "expired":
      return "This invite has expired. Ask the household owner to send a new one.";
    case "revoked":
      return "This invite was revoked by the household owner.";
    case "accepted":
      return "This invite has already been accepted.";
    case "wrong_email":
      return invite?.invited_email
        ? `This invite was sent to ${invite.invited_email}. Please sign in with that Google account.`
        : "This invite is for a different email address.";
    default:
      return "Could not process this invitation.";
  }
}
